import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { specials } from "@/mocks/specials";
import { reviews } from "@/mocks/reviews";
import { movies } from "@/mocks/movies";
import usePageMeta from "@/hooks/usePageMeta";

export default function Special() {
  const [searchParams] = useSearchParams();
  const initial = searchParams.get("id");
  const [activeId, setActiveId] = useState(
    initial && specials.some((s) => s.id === initial) ? initial : specials[0]?.id ?? ""
  );

  usePageMeta({
    title: "影评专题 - 爱的色放电影",
    description:
      "爱的色放电影影评专题，聚合爱情电影的主题策划、深度解析与观影片单，带你读懂银幕背后的深情。",
    keywords: "影评专题,爱情电影影评,电影专题,观影指南,爱的色放电影",
  });

  const active = useMemo(
    () => specials.find((s) => s.id === activeId) ?? null,
    [activeId]
  );

  const activeReviews = useMemo(() => {
    if (!active) return [];
    return reviews.filter((r) => active.reviewIds.includes(r.id));
  }, [active]);

  const relatedTopics = useMemo(
    () => specials.filter((s) => s.id !== activeId).slice(0, 3),
    [activeId]
  );

  if (!active) {
    return (
      <div className="w-full px-4 md:px-10 pt-32 pb-20">
        <div className="max-w-[1440px] mx-auto flex flex-col items-center justify-center text-center py-20 rounded-lg bg-background-50 border border-background-200">
          <i className="ri-article-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 font-heading font-bold text-xl text-foreground-950">
            当前无专题内容，敬请期待
          </h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const findMovie = (id: string) => movies.find((m) => m.id === id);

  return (
    <div className="pb-12">
      {/* topic cover */}
      <section className="relative w-full h-[320px] md:h-[440px] overflow-hidden">
        <img
          src={active.cover}
          alt={`${active.title} 专题主图`}
          title={`${active.title} - 影评专题`}
          className="w-full h-full object-top object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/35 to-foreground-950/20"></div>
        <div className="relative z-10 h-full w-full px-4 md:px-10 flex flex-col justify-end pb-8 md:pb-12">
          <div className="max-w-[1440px] mx-auto w-full">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500 text-foreground-950 text-xs font-medium mb-3">
              <i className="ri-book-open-line"></i>
              影评专题
            </span>
            <h1 className="font-heading font-bold text-background-50 text-2xl md:text-4xl max-w-3xl">
              {active.title}
            </h1>
            <p className="mt-3 text-background-200 text-sm md:text-lg max-w-2xl">
              {active.subtitle}
            </p>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-10 pt-8">
        <div className="max-w-[1440px] mx-auto">
          {/* topic tabs */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-8">
            {specials.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveId(s.id)}
                className={`px-4 py-2 rounded-full text-sm cursor-pointer border transition-colors whitespace-nowrap ${
                  s.id === activeId
                    ? "bg-primary-500 border-primary-500 text-background-50"
                    : "bg-background-50 border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {s.title.split("：")[0]}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              {/* intro */}
              <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-5 md:p-6 mb-8">
                <h2 className="flex items-center gap-2 font-heading font-semibold text-base text-secondary-900 mb-3">
                  <i className="ri-quill-pen-line"></i>
                  专题导语
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-loose">
                  {active.intro}
                </p>
              </div>

              {/* reviews */}
              <div className="flex items-center gap-3 mb-5">
                <span className="w-1 h-6 rounded-full bg-primary-500"></span>
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                  专题影评
                </h2>
                <span className="text-sm text-foreground-500">{activeReviews.length} 篇</span>
              </div>

              {activeReviews.length === 0 ? (
                <div className="rounded-lg bg-background-50 border border-background-200 py-12 text-center text-sm text-foreground-500">
                  暂无影评
                </div>
              ) : (
                <div className="flex flex-col gap-5">
                  {activeReviews.map((r) => {
                    const m = findMovie(r.movieId);
                    return (
                      <Link
                        key={r.id}
                        to={`/detail/${r.movieId}`}
                        className="group flex flex-col sm:flex-row gap-4 p-4 md:p-5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                      >
                        {m && (
                          <div className="w-full sm:w-32 h-40 sm:h-44 shrink-0 rounded-md overflow-hidden bg-background-200">
                            <img
                              src={m.poster}
                              alt={`${m.title} 海报`}
                              title={`${m.title} - 专题影评`}
                              className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0 flex flex-col">
                          <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-950 group-hover:text-primary-600 transition-colors leading-snug">
                            {r.title}
                          </h3>
                          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-3">
                            {r.excerpt}
                          </p>
                          <div className="mt-auto pt-3 flex items-center gap-3 text-xs text-foreground-500">
                            <span className="flex items-center gap-1">
                              <i className="ri-user-3-line"></i>
                              {r.author}
                            </span>
                            <span>{r.date}</span>
                            <span className="ml-auto flex items-center gap-1 text-primary-500">
                              <i className="ri-heart-3-line"></i>
                              {r.likes}
                            </span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* related topics */}
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-28">
                <h2 className="flex items-center gap-2 font-heading font-semibold text-base text-foreground-950 mb-4">
                  <i className="ri-links-line text-primary-500"></i>
                  相关专题推荐
                </h2>
                <div className="flex flex-col gap-4">
                  {relatedTopics.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        setActiveId(s.id);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className="group text-left rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <div className="w-full h-28 overflow-hidden bg-background-200">
                        <img
                          src={s.cover}
                          alt={`${s.title} 专题`}
                          title={`${s.title} - 相关专题`}
                          className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <p className="font-heading font-semibold text-sm text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {s.title}
                        </p>
                        <p className="mt-1 text-xs text-foreground-500 line-clamp-2">
                          {s.subtitle}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}